import { NgClass } from '@angular/common';
import { Component, inject, output, signal } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { IPayment } from '@webapp/quoter/interfaces/payment.interface';
import { QuoterStoreService } from '@webapp/quoter/store/quoter-store.service';
import { ButtonComponent } from '@webapp/ui';

@Component({
  selector: 'app-add-card',
  standalone: true,
  imports: [ReactiveFormsModule, NgClass, ButtonComponent],
  templateUrl: './add-card.component.html',
  styleUrls: ['./add-card.component.scss'],
})
export class AddCardComponent {
  private readonly quoterStateService = inject(QuoterStoreService);
  private readonly formBuilder = inject(FormBuilder);

  readonly quoterState = this.quoterStateService.state.asReadonly();
  addPayment = output<IPayment>();
  readonly submitted = signal(false);

  paymentForm: FormGroup;
  cardType = 'Desconocido';
  cardLogo: string | null = 'images/credit-card.svg';

  constructor() {
    this.paymentForm = this.formBuilder.group({
      cardNumber: [
        '',
        [Validators.required, this.creditCardValidator.bind(this)],
      ],
      cardHolder: ['', Validators.required],
      expirationDate: [
        '',
        [
          Validators.required,
          Validators.pattern(/^(0[1-9]|1[0-2])\/\d{2}$/),
          this.expirationDateValidator.bind(this),
        ],
      ],
      cvv: [
        '',
        [Validators.required, Validators.minLength(3), Validators.maxLength(4)],
      ],
    });
  }

  setPayment(): void {
    const { cardNumber, cardHolder, expirationDate, cvv } =
      this.paymentForm.controls;
    const formattedNumber = cardNumber.value?.replaceAll(' ', '') ?? '';
    const formattedDate = this.formatExpirationDate(expirationDate.value ?? '');

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const payment: IPayment = {
      name: cardHolder.value ?? '',
      number: formattedNumber,
      securityNumber: cvv.value ?? '',
      expiresAt: formattedDate,
      type: this.cardType,
    };
  }

  formatExpirationDate(date: string): string {
    const [month, year] = date.split('/');
    return `20${year}/${month}`;
  }

  onCardNumberInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const raw = input.value.replace(/\D/g, '');
    const formatted = this.formatCardNumber(raw);

    input.value = formatted;
    this.paymentForm
      .get('cardNumber')
      ?.setValue(formatted, { emitEvent: false });
    this.detectCardType(formatted);
  }

  onExpirationDateInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const raw = input.value.replace(/\D/g, '');
    const formatted =
      raw.length >= 3 ? `${raw.slice(0, 2)}/${raw.slice(2, 4)}` : raw;

    input.value = formatted;
    this.paymentForm
      .get('expirationDate')
      ?.setValue(formatted, { emitEvent: false });
  }

  formatCardNumber(value: string): string {
    return value.replace(/(\d{4})(?=\d)/g, '$1 ');
  }

  detectCardType(cardNumber: string): void {
    const types = [
      { type: 'Visa', regex: /^4/ },
      { type: 'Mastercard', regex: /^5[1-5]/ },
      { type: 'AmericanExpress', regex: /^3[47]/ },
      { type: 'Discover', regex: /^6(?:011|5)/ },
      { type: 'DebitCard', regex: /^(?:622|504)/ },
    ];

    const match = types.find((t) => t.regex.test(cardNumber));
    this.cardType = match?.type ?? 'Desconocido';
    this.cardLogo = this.getCardLogo(this.cardType);
  }

  getCardLogo(type: string): string | null {
    const logos: Record<string, string> = {
      Visa: 'images/visa.svg',
      Mastercard: 'images/mastercard.svg',
      AmericanExpress: 'images/amex.svg',
      Discover: 'images/credit-card.svg',
      DebitCard: 'images/credit-card.svg',
      Desconocido: 'images/credit-card.svg',
    };

    return logos[type] ?? null;
  }

  expirationDateValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value ?? '';
    const isValidFormat = /^(0[1-9]|1[0-2])\/\d{2}$/.test(value);
    if (!isValidFormat) return { invalidFormat: true };

    const [month, year] = value.split('/').map(Number);
    const now = new Date();
    const currentMonth = now.getMonth() + 1;
    const currentYear = now.getFullYear() % 100;

    if (year < currentYear || (year === currentYear && month < currentMonth)) {
      return { expiredDate: true };
    }

    return null;
  }

  creditCardValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value?.replace(/\D/g, '') ?? '';
    if (!value || !/^\d+$/.test(value)) return { invalidFormat: true };

    let sum = 0;
    let double = false;

    for (let i = value.length - 1; i >= 0; i--) {
      let digit = parseInt(value[i], 10);
      if (double) {
        digit *= 2;
        if (digit > 9) digit -= 9;
      }
      sum += digit;
      double = !double;
    }

    return sum % 10 === 0 ? null : { invalidCard: true };
  }

  submit(): void {
    this.submitted.set(true);
    if (!this.paymentForm.valid) return;
    const { cardNumber, cardHolder, expirationDate, cvv } =
      this.paymentForm.controls;
    const formattedNumber = cardNumber.value?.replaceAll(' ', '') ?? '';
    const formattedDate = this.formatExpirationDate(expirationDate.value ?? '');

    const payment: IPayment = {
      name: cardHolder.value ?? '',
      number: formattedNumber,
      securityNumber: cvv.value ?? '',
      expiresAt: formattedDate,
      type: this.cardType,
    };

    this.addPayment.emit(payment);
  }

  shouldShowError(controlName: string, errorName: string): boolean {
    const control = this.paymentForm.get(controlName);
    return (
      (this.submitted() ||
        ((control?.touched ?? false) && (control?.dirty ?? false))) &&
      control?.hasError(errorName) === true
    );
  }
}
